if (!customElements.get('floating-header')) {
  customElements.define('floating-header', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.timers = new Map();
      this.menus = [...this.querySelectorAll('details')];
      this.toggle = this.querySelector('.floating-header__toggle');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.abort.signal });
      this.menus.forEach(menu => {
        const summary = menu.querySelector('summary');
        summary.setAttribute('aria-expanded', 'false');
        listen(summary, 'click', event => {
          event.preventDefault();
          clearTimeout(this.hoverTimer);
          menu.open && !menu.classList.contains('is-closing') ? this.close(menu) : this.open(menu);
        });
        listen(summary, 'keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.open(menu);
            menu.querySelector('.floating-header__panel a')?.focus();
          }
        });
        listen(menu, 'pointerenter', event => {
          if (event.pointerType !== 'mouse' || this.mobile.matches) return;
          clearTimeout(this.leaveTimer);
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => this.open(menu), 100);
        });
        listen(menu, 'pointerleave', () => clearTimeout(this.hoverTimer));
      });
      listen(this, 'pointerenter', () => clearTimeout(this.leaveTimer));
      listen(this, 'pointerleave', event => {
        if (event.pointerType === 'mouse' && !this.mobile.matches) {
          this.leaveTimer = setTimeout(() => {
            if (!this.contains(document.activeElement)) this.closeAll();
          }, 180);
        }
      });
      listen(this.toggle, 'click', event => {
        const open = !this.classList.contains('is-mobile-open');
        this.setMobileOpen(open);
        if (open && event.detail === 0) this.querySelector('.floating-header__tab')?.focus();
      });
      listen(document, 'pointerdown', event => {
        if (!this.contains(event.target)) {
          this.closeAll();
          this.setMobileOpen(false);
        }
      });
      listen(this, 'keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        if (this.mobile.matches) {
          this.setMobileOpen(false);
          this.toggle.focus();
        } else {
          const active = this.menus.find(menu => menu.open);
          this.closeAll();
          active?.querySelector('summary').focus();
        }
      });
      listen(this, 'focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.closeAll();
          this.setMobileOpen(false);
        }
      });
      listen(this.mobile, 'change', () => {
        this.closeAll(true);
        this.setMobileOpen(false);
      });
      listen(window, 'scroll', () => this.measure());
      listen(window, 'resize', () => this.measure());
      listen(this, 'shopify:block:select', event => {
        const menu = event.target.closest('details');
        if (menu) {
          if (this.mobile.matches) this.setMobileOpen(true);
          this.open(menu);
        }
      });
      listen(this, 'shopify:block:deselect', () => this.closeAll());
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);
      this.measure();
    }
    measure() {
      const box = this.getBoundingClientRect();
      this.classList.toggle('is-scrolled', window.scrollY > 25);
      this.style.setProperty('--fh-panel-top', `${Math.max(0, box.bottom)}px`);
      document.documentElement.style.setProperty('--header-height', `${box.height}px`);
      document.documentElement.style.setProperty('--header-height-without-bottom-nav', `${box.height}px`);
    }
    open(menu) {
      clearTimeout(this.leaveTimer);
      this.menus.forEach(other => { if (other !== menu) this.close(other, true); });
      clearTimeout(this.timers.get(menu));
      this.timers.delete(menu);
      menu.classList.remove('is-closing');
      menu.querySelector('.floating-header__panel').inert = false;
      menu.open = true;
      menu.querySelector('summary').setAttribute('aria-expanded', 'true');
      this.measure();
    }
    close(menu, immediate = false) {
      clearTimeout(this.timers.get(menu));
      const finish = () => {
        menu.open = false;
        menu.classList.remove('is-closing');
        this.timers.delete(menu);
      };
      menu.querySelector('summary').setAttribute('aria-expanded', 'false');
      menu.querySelector('.floating-header__panel').inert = true;
      if (!menu.open || immediate || this.reducedMotion.matches) return finish();
      menu.classList.add('is-closing');
      this.timers.set(menu, setTimeout(finish, 160));
    }
    closeAll(immediate = false) {
      clearTimeout(this.hoverTimer);
      this.menus.forEach(menu => this.close(menu, immediate));
    }
    setMobileOpen(open) {
      this.classList.toggle('is-mobile-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
      this.toggle.setAttribute('aria-label', open ? this.toggle.dataset.closeLabel : this.toggle.dataset.openLabel);
      if (open) {
        if (this.menus[0]) this.open(this.menus[0]);
      } else this.closeAll(true);
      this.measure();
    }
    disconnectedCallback() {
      this.abort.abort();
      this.resizeObserver.disconnect();
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
      this.timers.forEach(timer => clearTimeout(timer));
    }
  });
}
